import isEmpty from 'lodash/isEmpty';
import {
  getAllOrdersFromOrdersSearch,
  getDecisionNodeAtPath,
  getRbiRefundCounts,
  getSelectedNestedSubItems,
  getSelectedOrderFromOrdersSearch,
  getNumberCustomerSupportTransactions,
  getSimplrAccountIdOfSelectedTicket,
  getBrandOfSelectedTicket,
} from '../../../extractFromState';
import { getSelectedLineItemsIds } from '../../../selectors/tools';
import { RBI_ORDER_STATUSES } from '../../../constants/rbi';
import { addEdgeToDecisionTreePath } from '../../searchActions';
import { DECISION_NODE_ACTIONS, RBI_DECISION_EDGES } from '../../../constants/decisionTree';
import * as ordersActions from '../OrdersSearchActions';
import {
  constructRbiRefundItemsList,
  countTotalItemsPoints,
  sortRbiPaidAndFreeItems,
  constructRbiCouponOrLoyaltyPointsItemsList,
} from '../../../util';
import { showErrorPopup } from '../../../components/popups/ErrorPopup/ErrorPopup';
import { countRefundsForPastPeriod, countOrderRemediationsForLastDays } from '../../../util/rbiUtil';
import { getRemediations } from '../../../api/orders/rbi';

export const processRbiOrderRefundItems = (isFullOrderRefund) => async (dispatch, getState) => {
  try {
    const state = getState();
    const selectedOrder = getSelectedOrderFromOrdersSearch(state);
    const selectedItemsIds = getSelectedLineItemsIds(state);
    const selectedNestedSubItems = getSelectedNestedSubItems(state);
    const refundsCount = getRbiRefundCounts(state);
    const decisionNode = getDecisionNodeAtPath(state);
    const allOrders = getAllOrdersFromOrdersSearch(state);

    if (isEmpty(selectedOrder)) {
      throw new Error('Selected order is empty');
    }

    // order has already been fully or partially refunded
    if (selectedOrder.status === RBI_ORDER_STATUSES.REFUND_SUCCESSFUL) {
      return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.ORDER_ALREADY_REFUNDED));
    }

    // FRAUD_ALERT
    if (selectedOrder.status === RBI_ORDER_STATUSES.KOUNT_DENIAL) {
      return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.FRAUD_ALERT));
    }

    // payment hasn't been processed correctly
    if (selectedOrder.status === RBI_ORDER_STATUSES.PAYMENT_ERROR) {
      return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.PAYMENT_ERROR));
    }

    if (selectedOrder.status === RBI_ORDER_STATUSES.REFUND_ERROR) {
      return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.REFUND_ERROR));
    }

    // customer reached the limit of refunds we can process
    const refundCountsLimit = decisionNode.refundCountsLimit && parseInt(decisionNode.refundCountsLimit);
    const checkLastWeeks = decisionNode.checkLastWeeks && parseInt(decisionNode.checkLastWeeks);
    const refundCountsLimitForHours =
      decisionNode.refundCountsLimitForHours && parseInt(decisionNode.refundCountsLimitForHours);
    const checkLastHours = decisionNode.checkLastHours && parseInt(decisionNode.checkLastHours);
    const refundPercentageThreshold =
      decisionNode.refundPercentageThreshold && parseInt(decisionNode.refundPercentageThreshold);

    if (refundPercentageThreshold && refundsCount.refundedPercentage >= refundPercentageThreshold) {
      return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.REFUND_PERCENTAGE_THRESHOLD_EXCEEDED));
    } else if (refundCountsLimit && checkLastWeeks) {
      const amountOfRefundsInLastWeeks = countRefundsForPastPeriod(allOrders, checkLastWeeks, 'weeks');

      if (amountOfRefundsInLastWeeks >= refundCountsLimit) {
        return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.MAX_REFUNDS_COUNT_LIMIT_EXCEEDED));
      }
    } else if (refundCountsLimit && refundsCount.refundOrdersCount >= refundCountsLimit) {
      return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.MAX_REFUNDS_COUNT_LIMIT_EXCEEDED));
    }

    if (refundCountsLimitForHours && checkLastHours) {
      const amountOfRefundsInLastHours = countRefundsForPastPeriod(allOrders, checkLastHours, 'hours');

      if (amountOfRefundsInLastHours >= refundCountsLimitForHours) {
        return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.MAX_REFUNDS_COUNT_LIMIT_EXCEEDED));
      }
    }

    const remediationsLimit = decisionNode.remediationsLimit;
    const remediationsCheckLastDays = decisionNode.remediationsCheckLastDays;
    if (remediationsLimit && remediationsCheckLastDays) {
      const amountOfRemediations = countOrderRemediationsForLastDays(selectedOrder, remediationsCheckLastDays);

      if (amountOfRemediations >= remediationsLimit) {
        return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.ORDER_HAS_REMEDIATIONS));
      }
    }

    let totalRefundAmount;
    let totalPointsRefundAmount;
    let selectedItemsAndSubItemsForRefund; // for partial refund tree only

    if (isFullOrderRefund) {
      // for full refund flow, refund totalPaidCents
      // TODO: add delivery charges once RBI backend is ready
      totalRefundAmount = selectedOrder.totalPaidCents;
      totalPointsRefundAmount = countTotalItemsPoints(selectedOrder.cart || []);
    } else {
      selectedItemsAndSubItemsForRefund = constructRbiRefundItemsList(
        selectedOrder,
        selectedItemsIds,
        selectedNestedSubItems
      );

      dispatch(ordersActions.setSelectedRbiItemsForRefund(selectedItemsAndSubItemsForRefund));
      // for partial refund flow, calculate total bbased on selected items
      totalRefundAmount = selectedItemsAndSubItemsForRefund.reduce((total, item) => {
        if (item.price && item.quantity) {
          total += item.price;
        }

        return total;
      }, 0);

      totalPointsRefundAmount = countTotalItemsPoints(selectedItemsAndSubItemsForRefund);

      // if refund amount of selected items is greater then totalPaidCents, refund totalPaidCents
      if (totalRefundAmount > selectedOrder.totalPaidCents) {
        totalRefundAmount = selectedOrder.totalPaidCents;
      }
    }

    // total refund amount is greater than allowed refund limit
    if (decisionNode.centsAmountLimit && totalRefundAmount >= parseInt(decisionNode.centsAmountLimit)) {
      return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.REFUND_AMOUNT_LIMIT_EXCEEDED));
    }

    dispatch(
      ordersActions.setRefundAmountDetails({
        totalCents: totalRefundAmount,
        totalPoints: totalPointsRefundAmount,
      })
    );

    // additional paths for partial refund tree to issue coupon codes for free items
    if (!isFullOrderRefund) {
      const { paidItemsCount, freeItemsCount } = sortRbiPaidAndFreeItems(selectedItemsAndSubItemsForRefund);

      if (paidItemsCount && freeItemsCount) {
        // free and paid items selected -> isssue a coupon then refund items then send macro
        return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.FREE_AND_PAID_ITEMS_SELECTED));
      } else if (!paidItemsCount && freeItemsCount) {
        // only free items(s) selected -> issue a coupon then send macro
        return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.FREE_ITEM_SELECTED));
      }
    } else {
      // in full refund tree, if no totalRefundAmount, order has only free item
      if (!totalRefundAmount) {
        return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.FREE_ITEM_SELECTED));
      }
    }

    // order or items are valid for refund
    dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.ORDER_VALID_FOR_REFUND));
  } catch (error) {
    return showErrorPopup(error.message, 'Error');
  }
};

export const processRbiOrderIssueCoupon = () => (dispatch, getState) => {
  const state = getState();
  const selectedOrder = getSelectedOrderFromOrdersSearch(state);
  const selectedItemsIds = getSelectedLineItemsIds(state);
  const selectedNestedSubItems = getSelectedNestedSubItems(state);

  const { remediationsLimit, checkLastDays } = getDecisionNodeAtPath(state);

  if (remediationsLimit && checkLastDays) {
    const amountOfRemediations = countOrderRemediationsForLastDays(selectedOrder, checkLastDays);

    if (amountOfRemediations >= remediationsLimit) {
      return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.ORDER_HAS_REMEDIATIONS));
    }
  }
  const selectedItemsAndSubItemsForCoupon = constructRbiCouponOrLoyaltyPointsItemsList(
    selectedOrder,
    selectedItemsIds,
    selectedNestedSubItems
  );
  dispatch(ordersActions.setSelectedRbiItemsForCoupon(selectedItemsAndSubItemsForCoupon));

  return dispatch(addEdgeToDecisionTreePath('order_valid_for_coupon'));
};

// Tim Hortons only
export const processRbiOrderIssueLoyaltyPoints = () => (dispatch, getState) => {
  const state = getState();
  const { maxTransactions, remediationsLimit, checkLastDays } = getDecisionNodeAtPath(state);
  const selectedOrder = getSelectedOrderFromOrdersSearch(state);
  const numberCustomerSupportTransactions = getNumberCustomerSupportTransactions(state);
  const selectedItemsIds = getSelectedLineItemsIds(state);
  const selectedNestedSubItems = getSelectedNestedSubItems(state);
  if (maxTransactions && numberCustomerSupportTransactions >= parseInt(maxTransactions)) {
    return dispatch(addEdgeToDecisionTreePath('max_transactions_limit_exceeded'));
  }

  if (isEmpty(selectedOrder)) {
    return dispatch(addEdgeToDecisionTreePath('exisitng_account_no_orders'));
  }

  if (remediationsLimit && checkLastDays) {
    const amountOfRemediations = countOrderRemediationsForLastDays(selectedOrder, checkLastDays);

    if (amountOfRemediations >= remediationsLimit) {
      return dispatch(addEdgeToDecisionTreePath(RBI_DECISION_EDGES.ORDER_HAS_REMEDIATIONS));
    }
  }

  const selectedItemsAndSubItemsForLoyaltyPoints = constructRbiCouponOrLoyaltyPointsItemsList(
    selectedOrder,
    selectedItemsIds,
    selectedNestedSubItems
  );
  dispatch(ordersActions.setSelectedRbiItemsForLoyaltyPoints(selectedItemsAndSubItemsForLoyaltyPoints));

  return dispatch(addEdgeToDecisionTreePath('order_valid_for_points'));
};

export const handleExistingAccountNoOrdersRbi = () => async (dispatch, getState) => {
  const state = getState();
  const decisionNode = getDecisionNodeAtPath(state);

  if (decisionNode.action === DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_LOYALTY_POINTS_ISSUE) {
    return dispatch(processRbiOrderIssueLoyaltyPoints());
  }

  return dispatch(addEdgeToDecisionTreePath('exisitng_account_no_orders'));
};

export const checkRbiRemediations = () => async (dispatch, getState) => {
  const state = getState();
  const decisionNode = getDecisionNodeAtPath(state);
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
  const brandName = getBrandOfSelectedTicket(state);
  const selectedOrder = getSelectedOrderFromOrdersSearch(state);

  const remediationsLimit = decisionNode.remediationsLimit;
  const checkLastDays = decisionNode.checkLastDays;
  const customerEmail = selectedOrder.userDetails.email;

  const remediations = await getRemediations({
    simplrAccountId,
    customerEmail,
    brandName,
    ...(checkLastDays && { lastDays: Number(checkLastDays) }),
  });

  if (remediations.length >= parseInt(remediationsLimit)) {
    return dispatch(addEdgeToDecisionTreePath('limit_exceeded'));
  }

  return dispatch(addEdgeToDecisionTreePath('success'));
};
